// ── THE MEMBER'S OWN SAVED CARDS AT CHECKOUT (FOUNDER CHECKOUT REQUEST 2026-10-27; B3 `/api/me/cards`) ─────────────────────────
// The same display-only shape the accounts app reads (apps/accounts …/member-cards.ts): brand, last four, expiry and whether it is the
// default — never a card number, CVC, customer id or fingerprint. The backend lists ONLY the signed-in member's own cards (owner-bound
// Stripe customer), and the checkout PaymentIntent is created on that same customer, so a listed `pm_…` id is what the member may
// choose to pay with. Choosing one is the member's explicit act; nothing is saved here (saving a new card is the accounts app's
// explicit "Add a card" flow, a Stripe SetupIntent).

export interface SavedCard {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
}
export type SavedCardsRead = { state: "ok"; cards: SavedCard[] } | { state: "none" } | { state: "unavailable" };

export const PM_ID_RE = /^pm_[A-Za-z0-9]{8,64}$/;
const obj = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const int = (v: unknown, lo: number, hi: number): v is number => typeof v === "number" && Number.isInteger(v) && v >= lo && v <= hi;

function parseCard(v: unknown): SavedCard | null {
  if (!obj(v) || typeof v.id !== "string" || !PM_ID_RE.test(v.id)) return null;
  if (typeof v.brand !== "string" || v.brand === "" || v.brand.length > 30) return null;
  if (typeof v.last4 !== "string" || !/^[0-9]{4}$/.test(v.last4)) return null;
  if (!int(v.expMonth, 1, 12) || !int(v.expYear, 2000, 2200) || typeof v.isDefault !== "boolean") return null;
  return { id: v.id, brand: v.brand, last4: v.last4, expMonth: v.expMonth, expYear: v.expYear, isDefault: v.isDefault };
}

/**
 * The backend's `{ cards: { state, cards, hasMore, note } }`, re-read strictly. `no_customer` is the member's honest empty list; anything
 * malformed is `unavailable` (never drawn as an empty list). Expired cards are dropped: they cannot pay.
 */
export function parseSavedCards(answer: unknown, now = new Date()): SavedCardsRead {
  const c = obj(answer) ? answer.cards : undefined;
  if (!obj(c) || typeof c.state !== "string" || !Array.isArray(c.cards) || c.cards.length > 20) return { state: "unavailable" };
  if (c.state === "no_customer") return c.cards.length === 0 ? { state: "none" } : { state: "unavailable" };
  if (c.state !== "ok") return { state: "unavailable" };
  const cards: SavedCard[] = [];
  for (const raw of c.cards) {
    const card = parseCard(raw);
    if (!card || cards.some((x) => x.id === card.id)) return { state: "unavailable" };
    cards.push(card);
  }
  if (cards.filter((x) => x.isDefault).length > 1) return { state: "unavailable" };
  const y = now.getUTCFullYear(), m = now.getUTCMonth() + 1;
  const live = cards.filter((x) => x.expYear > y || (x.expYear === y && x.expMonth >= m));
  return live.length ? { state: "ok", cards: live } : { state: "none" };
}

/** "Visa •••• 4242 · exp 04/31" */
export function savedCardLabel(card: SavedCard): string {
  const brand = card.brand.charAt(0).toUpperCase() + card.brand.slice(1);
  return `${brand} •••• ${card.last4} · exp ${String(card.expMonth).padStart(2, "0")}/${String(card.expYear % 100).padStart(2, "0")}`;
}

// ── OWNER-SCOPED SAVED CARDS (PR #458 review F2) ────────────────────────────────────────────────────────────────────────────────
// A saved-card list and the member's explicit choice belong to ONE verified session identity (the bearer, or the server-verified
// cookie session's identity generation — `sessionIdentityOf`), never to "someone is signed in". They are kept WITH the owner they
// were read or made for, and only used while that owner is still the session: signed-in A → signed-in B (no signed-out moment
// between) shows none of A's cards or choice, and a late answer for A is dropped. The identity is compared in memory only.
export interface OwnedSavedCards {
  owner: string;
  cards: SavedCard[];
}
export interface OwnedSavedChoice {
  owner: string;
  id: string;
}

/** The cards to show for `owner` now: only a list read for that same owner (anything else, or no owner, is no list). */
export function savedCardsFor(owner: string | null, read: OwnedSavedCards | null): SavedCard[] {
  return owner && read?.owner === owner ? read.cards : [];
}

/** The member's chosen card for `owner` now: only a choice made by that owner, of a card in that owner's list; otherwise the new card. */
export function savedChoiceFor(owner: string | null, read: OwnedSavedCards | null, choice: OwnedSavedChoice | null): string | null {
  return owner && choice?.owner === owner && savedCardsFor(owner, read).some((c) => c.id === choice.id) ? choice.id : null;
}

/**
 * Ask `/api/me/cards` for `owner`'s list. The answer is delivered only if this request is still live AND `owner` is still the session
 * when it lands (a late answer for a previous member is dropped). Returns the cancel for the effect cleanup.
 */
export function loadOwnedSavedCards({
  owner,
  ownerNow,
  fetchImpl,
  onRead,
}: {
  owner: string;
  ownerNow: () => string | null;
  fetchImpl: typeof fetch;
  onRead: (read: OwnedSavedCards) => void;
}): () => void {
  let live = true;
  void fetchImpl("/api/me/cards", { cache: "no-store", credentials: "same-origin" })
    .then((r) => r.json().catch(() => null) as Promise<SavedCardsRead | null>)
    .then((read) => {
      if (!live || ownerNow() !== owner) return;
      // only well-formed owner-bound ids are offered; anything else is no list (the new-card entry is always there)
      const cards = read?.state === "ok" && Array.isArray(read.cards) ? read.cards.filter((c) => PM_ID_RE.test(c.id)) : [];
      onRead({ owner, cards });
    })
    .catch(() => undefined);
  return () => {
    live = false;
  };
}
